import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BaseRestService } from './base-rest.service';
import { IStatusList, Status, StatusCategoryLink } from '../models/status';
import { StateService } from './state.service';
import { CategoryService } from './category.service';
import { OnDestroy } from '@angular/core';
import { EventService } from './event.service';

@Injectable({ providedIn: 'root' })
export class StatusService extends BaseRestService<Status, IStatusList> implements OnDestroy {

  onStateChanged$: Subscription;
  onCategoryChanged$: Subscription;

  constructor(http: HttpClient, private stateService: StateService, private categoryService: CategoryService, private eventService: EventService, protected router: Router) {
    super(StatusService, http, 'api/statuses', 'status', router);

    // enable caching
    this.cacheEnabled = true;

    // when state is changed, updated any related statuses
    this.onStateChanged$ = this.stateService.onItemChanged.subscribe((state) => {
      if (state) {
        this.cache.filter((i) => i.stateId === state.id).map((item) => item.state = state);
      }
    });

    this.onCategoryChanged$ = this.categoryService.onItemChanged.subscribe((category) => {
      const cache = this.getCache();
      const hydrate$ = [];
      cache.forEach((s) => {
        const link: StatusCategoryLink = s.statusCategoryLinks.find((x) => x.categoryId === category.id);
        if (link) {
          link.category = undefined;
          hydrate$.push(this.hydrate(s));
        }
      });
      if (hydrate$.length > 0) {
        forkJoin(hydrate$).pipe(take(1)).subscribe();
      }
    });
  }

  ngOnDestroy() {
    this.onStateChanged$.unsubscribe();
  }

  save(entity: Status, successMessage?: string) {
    if (entity) {
      entity.state = null;
      // make sure category link objects are not set before save...
      entity.statusCategoryLinks.map((i) => {
        i.category = null;
        i.status = null;
        return i;
      });
    }

    this.onAfterSave.pipe(take(1)).subscribe((_) => {
      // if status has been changed, update stats
      this.eventService.updateSubjectStats();
      this.eventService.updateReferralStats();
    });

    super.save(entity, successMessage);
  }

  hydrate(item: Status): Observable<Status> {
    const hydrate$ = [];
    hydrate$.push(of(item));
    if (item.statusCategoryLinks && (item.statusCategoryLinks.length > 0)) {
      item.statusCategoryLinks.map((i) => {
        if ((!i.category) || (i.category.id !== i.categoryId)) {
          hydrate$.push(this.categoryService.getById(i.categoryId).pipe(take(1), map((category) => {
            i.category = category;
            return item;
          })));
        }
      });
    }
    if ((!item.state) || (item.state.id !== item.stateId)) {
      hydrate$.push(this.stateService.getById(item.stateId).pipe(take(1), map((state) => {
        item.state = state;
        return item;
      })));
    }

    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        item.categories = (item.statusCategoryLinks) ?
          item.statusCategoryLinks.sort((a, b) => a.category.description.localeCompare(b.category.description)).map((i) => i.category.description).join(', ') : '';
        return item;
      }));
    }
    else {
      return of(item);
    }

  }

  getDescription(id: number): string {
    const cache = this.getCache();
    const status = cache.find((x) => (x.id === id));
    return (status) ? status.name : id.toString();
  }

  public getStatusListForCategory(categoryId: number): Observable<Status[]> {
    return this.getAllData()
    .pipe(take(1),map((response) => {
      if (response?.length > 0) {
        const statuses:Status[] = response.filter(
          (status) => status.hideRecord !== true
        );
        // tslint:disable-next-line:only-arrow-functions
        statuses.sort((a, b) => a.name.localeCompare(b.name));

        if (categoryId==undefined || categoryId<0)
          return statuses;

        var filteredStatuses:Status[] = statuses.filter((status) => {
          var rc = (
            !categoryId ||
            status.statusCategoryLinks ==undefined ||
            (categoryId && 
              (!status.statusCategoryLinks ||
                status.statusCategoryLinks.length === 0 ||
                status.statusCategoryLinks
                  .map((i) => i.categoryId)
                  .includes(categoryId)))
          );
          return rc;
        });
        return filteredStatuses;
      }
    }));
  }
}
